import { useContext } from 'react';
import PoolDataContext from '../contexts/PoolDataContext.jsx';

/**
 * Hook to access pool data from PoolDataContext
 * @returns {Object} Context value containing:
 * @returns {string} poolSlug - Current pool slug identifier
 * @returns {Array} blocks - Array of all fetched block objects
 * @returns {Object} blocksMeta - Metadata about pagination state:
 *   - {number} totalFetched - Total number of blocks fetched so far
 *   - {boolean} hasMore - Whether more blocks are available to load
 *   - {number} currentPage - Current page number (0-indexed)
 *   - {boolean} isLoadingMore - Whether a loadMoreBlocks request is in progress
 *   - {number|null} lastMinHeight - Minimum block height for next page (null if no more pages)
 * @returns {Function} loadMoreBlocks - Function to load the next page of blocks on demand
 *   Call this when user scrolls to bottom or clicks "Load More" button.
 *   Returns Promise<void> that resolves when loading completes.
 * @returns {Object} poolInfo - Pool information and statistics
 * @returns {Object} analysis - Pool performance analysis results
 * @returns {Object} predictionData - Prediction curve data
 * @returns {Array} selectedBlocks - Currently selected blocks
 * @returns {boolean} isLoading - Whether initial data fetch is in progress
 * @returns {Error|null} error - Error object if fetch failed
 * @returns {Date|null} lastRefresh - Timestamp of last successful refresh
 * @returns {Function} setSelectedBlocks - Function to update selected blocks
 * @returns {Function} refreshData - Function to force refresh all data
 * @returns {Function} setPoolSlug - Function to change the pool slug
 * @returns {boolean} isPollingEnabled - Whether automatic polling is enabled
 * @returns {Function} setIsPollingEnabled - Function to enable/disable polling
 * @returns {Object} settings - Analysis settings (analysisWindowHours, hashrateWeight, etc.)
 * @returns {Function} updateSettings - Function to update settings and trigger refresh
 * @returns {Object|null} optimizationResults - Results from parameter optimization:
 *   - {Object} bestParams - Best parameter combination { outlierPThreshold, hashrateWeight }
 *   - {number} bestMAE - Lowest MAE achieved (seconds)
 *   - {Array} allResults - All tested combinations sorted by MAE
 *   - {number} totalCombinations - Number of combinations tested
 * @returns {Function} runOptimization - Async function to run parameter optimization.
 *   Returns Promise<Object|null> with optimization results.
 *   May take 5-10 seconds for large datasets.
 * 
 * @example
 * // Basic usage
 * const { blocks, blocksMeta, loadMoreBlocks } = usePoolData();
 * 
 * // Conditionally render "Load More" button
 * {blocksMeta.hasMore && !blocksMeta.isLoadingMore && (
 *   <button onClick={loadMoreBlocks}>
 *     Load More Blocks
 *   </button>
 * )}
 * 
 * // Infinite scroll pattern
 * useEffect(() => {
 *   const handleScroll = () => {
 *     if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 1000) {
 *       if (blocksMeta.hasMore && !blocksMeta.isLoadingMore) {
 *         loadMoreBlocks();
 *       }
 *     }
 *   };
 *   window.addEventListener('scroll', handleScroll);
 *   return () => window.removeEventListener('scroll', handleScroll);
 * }, [blocksMeta.hasMore, blocksMeta.isLoadingMore, loadMoreBlocks]);
 * 
 * // Run parameter optimization
 * const { runOptimization, optimizationResults, isLoading } = usePoolData();
 * 
 * const handleOptimize = async () => {
 *   const result = await runOptimization();
 *   if (result) {
 *     console.log('Best parameters:', result.bestParams);
 *     console.log('Best MAE:', result.bestMAE);
 *   }
 * };
 * 
 * // Display optimization results
 * {optimizationResults && (
 *   <div>
 *     <p>Optimal outlier threshold: {optimizationResults.bestParams.outlierPThreshold}</p>
 *     <p>Optimal hashrate weight: {optimizationResults.bestParams.hashrateWeight}</p>
 *     <p>Best MAE: {optimizationResults.bestMAE.toFixed(2)}s</p>
 *   </div>
 * )}
 */
export function usePoolData() {
  const context = useContext(PoolDataContext);
  
  if (!context) {
    throw new Error('usePoolData must be used within a PoolDataProvider');
  }
  
  return context;
}



